import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { STORAGE_KEYS_CONSTANTS } from '@core/constants';
import { ILanguage, INavigation } from '@core/models';
import { Browser, Language, Storage } from '@core/services';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageUtil } from '@shared/utils';
import { firstValueFrom } from 'rxjs';
import { Notifications } from '../notifications/notifications.component';
import { LoginButton } from '../login-button/login-button.component';

/**
 * ### **MainNavigation**
 *
 * Menú de navegación de la aplicación: enlaces principales, notificaciones, acceso de
 * usuario y selector de idioma.
 *
 * Se renderiza en fila dentro de la cabecera en escritorio y apilado dentro del bottom
 * sheet en móvil; el propio componente resuelve ambas disposiciones por CSS.
 * */
@Component({
  selector: 'app-main-navigation',
  imports: [RouterModule, TranslateModule, Notifications, LoginButton, ReactiveFormsModule],
  templateUrl: './main-navigation.component.html',
  styleUrl: './main-navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainNavigation implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly languageService = inject(Language);
  private readonly storage = inject(Storage);
  private readonly browser = inject(Browser);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Variable de tipo {@link INavigation} que contiene la información de los items para el menú de navegación, esta información llega desde un componete externo o padre
   */
  readonly navigation = input<INavigation[]>([]);

  /** variable de tipo TemplateRef, tiene la caracteristica de que la información llega desde un componente externo al ser @Input()*, por otro lado este botón es el
   * que aparecera en los dispositivos moviles*/
  readonly buttonTemplate = input<TemplateRef<any> | null>(null);

  readonly closeNavigation = output<any>();

  /** variable de tipo string  que contendra el idioma por defecto*/
  private defaultLang!: string;

  /** variable de tipo control que detectara el cambio de un valor en un grupo de datos, en este caso el idioma que selecciona el usuario */
  protected readonly languageControl = signal<FormControl>(new FormControl());

  /** Variable de tipo boolean que define el comportamiento del botón que visualizará el menú en los dispositivos moviles  */
  private toggleNavbar!: boolean;

  protected readonly languages = signal<ILanguage[]>([]);

  /**
   * @param translateService parametro que permite hacer uso del servicio de traducciones
   */
  constructor() {}

  /**
   * hook de angular que carga o inicializa los componentes a la hora de iniciar la carga de la app.
   */
  ngOnInit() {
    this.defaultLang = this.translateService.getBrowserLang() || 'es';
    this.languageControl().setValue(LanguageUtil.getStorageLanguage() || this.defaultLang);
    this.languageChange();
    this.toggleNavbar = false;
    this.getLanguages();
  }

  /**
   * Método que detecta el cambio de lenguaje,
   * y realiza una recarga de la página con el lenguaje seleccionado.
   * */
  languageChange(): void {
    this.languageControl()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.translateService.use(value);
        LanguageUtil.setStorageLanguage(value);
        this.storage.removeSession(STORAGE_KEYS_CONSTANTS.HOME);

        if (!this.browser.isBrowser) return;
        window.location.reload();
      });
  }

  /**
   * Función encargada de obtener los idiomas existen actualmente.
   */
  private async getLanguages(): Promise<void> {
    try {
      const response = await firstValueFrom(this.languageService.getLanguages2());
      this.languages.set(response.results as ILanguage[]);

      // console.log({ languages: this.languages(), value: this.languageControl().value });
    } catch (error) {
      console.error(error);
    }
  }
}
